import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  createContext,
  useContext,
  Dispatch,
  SetStateAction,
  RefObject,
} from "react";
import {
  ProcessPictureElement,
  ProcessPictureEvent,
  ProcessPictureInitEvent,
  registerComponents,
  ParsedPointId,
} from "@fidelix/fx-margaret";
import {
  useDebouncedHandler,
  useParsedSearchParams,
} from "@fidelix/fx-miranda";
import { t } from "@lingui/core/macro";
import { Point } from "~point/point.model";
import { usePointsGFXQuery } from "~point/point.queries";
import { useQueryClient, UseQueryResult } from "@tanstack/react-query";
import {
  useGraphicsAttachmentsQuery,
  useGraphicsQuery,
} from "./graphics.queries";
import { isExternalIdIncluded, parsePointName } from "./graphics.utils";
import { Graphics, GraphicsAttachment } from "./graphics.model";
import {
  HistoryPreviewDataService,
  useHistoryPreviewData,
} from "./history/history-replay-cached-data.utils";
import { useGraphicsNavigation } from "./graphics-navigation.hook";
import { useUserContext } from "../user/user-context";
import { hasPermission } from "../utils/permission-helper";
import { Permissions } from "../const";

export type PointWithGraphicMetaData = Point &
  ParsedPointId & { graphicNodeId?: string };

type FocusEvent = "x-hover" | "x-click";
type FocusedPoint = {
  point?: Partial<PointWithGraphicMetaData>;
  eventType?: FocusEvent;
  linkId?: string;
};
type FocusPayload = {
  type: string;
  detail: ProcessPictureEvent["detail"];
  target: HTMLDivElement | null;
};

type Values = {
  setElement: Dispatch<SetStateAction<ProcessPictureElement | undefined>>;
  focusedPoint?: FocusedPoint;
  pointsQuery: UseQueryResult<
    {
      points: Point[];
      pagination: {
        total: number;
        totalPages: number;
        page: number;
        pageSize: number;
      };
    },
    Error
  >;
  triggerRect?: DOMRect;
  graphics?: Graphics;
  attachment?: GraphicsAttachment;
  attachments?: GraphicsAttachment[];
  setFocusedPoint: Dispatch<SetStateAction<FocusedPoint | undefined>>;
  sustainFocus: RefObject<boolean>;
  element: ProcessPictureElement | undefined;
  isGraphicsLoading?: boolean;
  cachedDataService: HistoryPreviewDataService | undefined;
};

const GraphicsContext = createContext<Values | null>(null);

// We need to register Margaret web components before we can use them
registerComponents();

export function GraphicsDataProvider({ children }) {
  const queryClient = useQueryClient();
  const sustainFocus = useRef(false);
  const [element, setElement] = useState<ProcessPictureElement | undefined>();
  const [graphicPoints, setGraphicPoints] = useState<string[]>([]);
  const [focusedPoint, setFocusedPoint] = useState<FocusedPoint>();
  const [triggerRect, setTriggerRect] = useState<DOMRect>();
  const { parsedParams, setSearchParams } = useParsedSearchParams({
    graphic: { type: "string", defaultValue: "" },
    graphicsId: { type: "number" },
  });
  const { user } = useUserContext();
  const { navigateEventHandler, history, setHistory } = useGraphicsNavigation();

  const cachedDataService = useHistoryPreviewData(element);

  const { graphicsId, graphic } = parsedParams;
  // Using staleTime infinity here so we don't unnecessarily refetch graphics
  // or attachments
  const { data: graphics, isFetching: isGraphicsFetching } = useGraphicsQuery(
    graphicsId!,
    {
      enabled: !!graphicsId,
      staleTime: Infinity,
    },
  ); // We know graphicsId exists hence using !

  const { data: attachments, isFetching: isAttachmentsFetching } =
    useGraphicsAttachmentsQuery(graphicsId!, {
      enabled: !!graphicsId,
      staleTime: Infinity,
    }); // We know graphicsId exists hence using !

  const pointsWithExternalId = useMemo(() => {
    if (!graphics || graphicPoints.length === 0) {
      // wait until graphics loaded
      return [];
    }
    const rawPointIds = graphicPoints.map((pointWithLimitIndex) => {
      const { pointBaseName } = parsePointName(pointWithLimitIndex);
      return pointBaseName;
    });

    // Remove # params and extract only point Name
    // v1-QZpOIWnI54.DEMO_CASCADE_RG#CONTROL#210 -> v1-QZpOIWnI54.DEMO_CASCADE_RG
    const pointIds = Array.from(
      rawPointIds.reduce<Set<string>>((set, pointId) => {
        const [pointName] = pointId.split("#");
        set.add(pointName);
        return set;
      }, new Set()),
    );

    const pointIdsWithExternalId = pointIds.filter(isExternalIdIncluded);
    const pointIdsWithoutExternalId = pointIds.filter(
      (pointId) => !isExternalIdIncluded(pointId),
    );

    if (pointIdsWithoutExternalId.length > 0 && !graphics.device) {
      alert(
        t`There are points without device ID.\n\n${pointIdsWithoutExternalId.join(", ")}`,
      );
    }

    // If pointId does not contain externalId then add device.externalId in it. It works for device graphics
    pointIdsWithoutExternalId.forEach((pointId) => {
      if (graphics.device) {
        pointIdsWithExternalId.push(`${graphics.device.externalId}.${pointId}`);
      }
    });
    return pointIdsWithExternalId;
  }, [graphics, graphicPoints]);

  const pointsQuery = usePointsGFXQuery(
    {
      externalPoints: pointsWithExternalId,
    },
    { enabled: !!pointsWithExternalId.length, staleTime: Infinity },
  );

  const resolvePoint = (detail: ProcessPictureEvent["detail"]) => {
    const { externalId, pointName } = detail;
    if (!externalId) {
      return;
    }
    return pointsQuery.data?.points.find(
      (p) =>
        p.device.externalId === externalId &&
        p.name.toUpperCase() === pointName.toUpperCase(),
    );
  };

  const setFocusFromPayload = (payload: FocusPayload, point?: Point) => {
    const { detail, target, type } = payload;
    const { pointName, limit, paramId, fdxshowsetvalue, externalId, id } =
      detail;
    const name = limit ? `${pointName}:${limit}` : pointName;

    if (target) {
      setFocusedPoint({
        point: {
          ...point,
          name,
          limit,
          paramId,
          fdxshowsetvalue,
          externalId,
          graphicNodeId: id,
        },
        eventType: type as FocusEvent,
      });
      // Store the element’s position instead of the DOM node (<process-picture>),
      // since re-renders can detach the original node and break popover positioning.
      setTriggerRect(target.getBoundingClientRect());
    } else {
      setTriggerRect(undefined);
      setFocusedPoint(undefined);
    }
  };

  const clickFocusHandler = (payload: FocusPayload) => {
    if (sustainFocus.current) return;
    const point = resolvePoint(payload.detail);
    if (cachedDataService?.selectionMode && point) {
      cachedDataService.togglePointSelectedStatus(
        point.id,
        payload.detail.fdxpntid,
      );
      return;
    }
    setFocusFromPayload(payload, point);
    sustainFocus.current = true;
  };

  const hoverFocusHandler = (payload: FocusPayload) => {
    if (sustainFocus.current) return;
    const point = resolvePoint(payload.detail);
    if (payload.target) {
      setFocusFromPayload(payload, point);
      return;
    }
    setTriggerRect(undefined);
    setFocusedPoint(undefined);
  };

  const debouncedHoverFocusHandler = useDebouncedHandler<FocusPayload>(
    hoverFocusHandler,
    300,
  );

  const canEditScadaLinks = hasPermission(
    Permissions.SCADAGRAPHICS_EDIT,
    user?.permissions ?? [],
  );

  const scadaLinkTooltipHandler = useCallback(
    async (event) => {
      const linkId = event.detail.fdxhref;
      const isScadaGraphic = graphics?.device === null;

      if (isScadaGraphic && canEditScadaLinks && linkId) {
        const target = event.detail.event?.target as HTMLElement | null;
        if (target) {
          setFocusedPoint({ linkId });
          setTriggerRect(target.getBoundingClientRect());
          sustainFocus.current = true;
          return;
        }
      }

      setFocusedPoint(undefined);
      setTriggerRect(undefined);
    },
    [canEditScadaLinks, graphics],
  );
  const focusEventHandler = (ev) => {
    if (ev.cancelable) ev.preventDefault();

    // check if hovered over a scada link
    if (ev.detail.fdxhref && ev.detail.fdxtype === "FdxText") {
      if (ev.detail.event) {
        scadaLinkTooltipHandler(ev);
      }
      return;
    }

    const payload: FocusPayload = {
      type: ev.type,
      detail: ev.detail,
      target: ev.detail.event?.target ?? null,
    };

    if (payload.type === "x-click") {
      clickFocusHandler(payload);
    } else {
      debouncedHoverFocusHandler(payload);
    }
  };

  useEffect(() => {
    if (element) {
      element.addEventListener("x-init", (e: Event) => {
        cachedDataService?.resetState();
        const ev = e as ProcessPictureInitEvent;
        setGraphicPoints(ev.detail);
      });
      element.addEventListener("x-back", () => {
        if (history.length > 0) {
          const newHistory = [...history];
          const prev = newHistory.pop();
          setHistory(newHistory);
          if (prev) {
            setSearchParams((params) => {
              params.set("graphicsId", prev.graphicId.toString());
              params.set("graphic", prev.file);
            });
          }
        }
      });
      element.addEventListener("x-hover", focusEventHandler);
      element.addEventListener("x-click", focusEventHandler);
      element.addEventListener("x-open", openEventHandler);
      element.addEventListener("x-navigate", navigateEventHandler);
      element.addEventListener("x-auth-expired", handleAuthExpire);
    }
    return () => {
      element?.removeEventListener("x-hover", focusEventHandler);
      element?.removeEventListener("x-click", focusEventHandler);
      element?.removeEventListener("x-open", openEventHandler);
      element?.removeEventListener("x-navigate", navigateEventHandler);
      element?.removeEventListener("x-auth-expired", handleAuthExpire);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [element, pointsQuery]);

  const handleAuthExpire = () => {
    queryClient.invalidateQueries({ queryKey: ["graphics-attachments"] });
  };

  const openEventHandler = (event) => {
    window.open(event.detail.fdxhref, "_blank");
  };

  const attachment = attachments?.find(
    (a) => a.name.toLowerCase() === graphic?.toLowerCase(),
  );

  const isGraphicsLoading =
    !graphics ||
    !attachments ||
    !attachment ||
    isGraphicsFetching ||
    isAttachmentsFetching;

  return (
    <GraphicsContext.Provider
      value={{
        pointsQuery,
        setElement,
        focusedPoint,
        triggerRect,
        graphics,
        attachment,
        attachments,
        setFocusedPoint,
        sustainFocus,
        element,
        isGraphicsLoading,
        cachedDataService,
      }}
    >
      {children}
    </GraphicsContext.Provider>
  );
}

export function useGraphicsContext() {
  const value = useContext(GraphicsContext);
  if (!value) {
    throw new Error(
      "useGraphicsContext has to be used under GraphicsDataProvider",
    );
  }
  return value;
}
